import { subDays, format, parse } from 'date-fns';

export type Category = 'Food' | 'Shopping' | 'Health' | 'Transport' | 'Housing' | 'Bills/Other' | 'Fun';

interface BrandInfo {
  merchant: string;
  category: Category;
  aliases?: string[];
}

// ============ MONEY PARSING ============

const CURRENCY_MAP: Record<string, string> = {
  '$': 'USD',
  '€': 'EUR',
  '£': 'GBP',
  '₴': 'UAH',
  'R$': 'BRL',
  'r$': 'BRL',
  'грн': 'UAH',
  'uah': 'UAH',
  'usd': 'USD',
  'eur': 'EUR',
  'gbp': 'GBP',
  'brl': 'BRL'
};

// Helper to find all amounts in text for multi-item parsing
function findAllAmounts(text: string): Array<{ amount: number; currency: string }> {
  const amounts: Array<{ amount: number; currency: string }> = [];
  
  // Find all currency patterns
  const patterns = [
    /(?<cur>[$€£₴]|R\$)\s*(?<amt>\d{1,6}([.,]\d{1,2})?)/g,
    /(?<amt>\d{1,6}([.,]\d{1,2})?)\s*(?<cur>[$€£₴]|R\$|грн)/g,
    /(?<cur>USD|EUR|GBP|UAH|BRL)\s*(?<amt>\d{1,6}([.,]\d{1,2})?)/gi,
    /(?<amt>\d{1,6}([.,]\d{1,2})?)\s*(?<cur>USD|EUR|GBP|UAH|BRL|грн)/gi,
  ];

  for (const pattern of patterns) {
    const matches = text.matchAll(pattern);
    for (const match of matches) {
      if (match.groups) {
        const amount = parseFloat(match.groups.amt.replace(',', '.'));
        let currency = match.groups.cur;
        
        // Map currency symbols/codes to standard codes
        if (currency.toLowerCase() === 'r$') currency = 'BRL';
        else if (CURRENCY_MAP[currency]) currency = CURRENCY_MAP[currency];
        else currency = currency.toUpperCase();
        
        if (amount > 0) {
          amounts.push({ amount, currency });
        }
      }
    }
  }

  return amounts;
}

export function parseMoney(text: string): { amount: number; currency: string } | null {
  // Check for multiple amounts (multi-item case)
  const allAmounts = findAllAmounts(text);
  
  if (allAmounts.length > 1) {
    // Multi-item: sum all amounts if they're in the same currency
    const firstCurrency = allAmounts[0].currency;
    const allSameCurrency = allAmounts.every(a => a.currency === firstCurrency);
    
    if (allSameCurrency) {
      const total = allAmounts.reduce((sum, a) => sum + a.amount, 0);
      return { amount: total, currency: firstCurrency };
    }
    
    // Different currencies: return the first one for now
    return allAmounts[0];
  }
  
  if (allAmounts.length === 1) {
    return allAmounts[0];
  }

  // Pattern 1: Symbol before amount ($45, €45, £45, ₴45, R$45)
  let match = text.match(/(?<cur>[$€£₴]|R\$)\s*(?<amt>\d{1,6}([.,]\d{1,2})?)/);
  if (match?.groups) {
    const amount = parseFloat(match.groups.amt.replace(',', '.'));
    let currency = match.groups.cur;
    if (currency.toLowerCase() === 'r$') currency = 'BRL';
    else currency = CURRENCY_MAP[currency] || 'EUR';
    if (amount > 0) return { amount, currency };
  }

  // Pattern 2: Amount before symbol (45$, 45€, 45£, 45₴, 45грн, 45R$)
  match = text.match(/(?<amt>\d{1,6}([.,]\d{1,2})?)\s*(?<cur>[$€£₴]|R\$|грн)/);
  if (match?.groups) {
    const amount = parseFloat(match.groups.amt.replace(',', '.'));
    let currency = match.groups.cur;
    if (currency.toLowerCase() === 'r$') currency = 'BRL';
    else currency = CURRENCY_MAP[currency] || 'EUR';
    if (amount > 0) return { amount, currency };
  }

  // Pattern 3: Currency code before amount (USD 45, EUR 45, BRL 45)
  match = text.match(/(?<cur>USD|EUR|GBP|UAH|BRL)\s*(?<amt>\d{1,6}([.,]\d{1,2})?)/i);
  if (match?.groups) {
    const amount = parseFloat(match.groups.amt.replace(',', '.'));
    const currency = match.groups.cur.toUpperCase();
    if (amount > 0) return { amount, currency };
  }

  // Pattern 4: Amount before currency code (45 USD, 45 EUR, 45 BRL, 45 грн)
  match = text.match(/(?<amt>\d{1,6}([.,]\d{1,2})?)\s*(?<cur>USD|EUR|GBP|UAH|BRL|грн)/i);
  if (match?.groups) {
    const amount = parseFloat(match.groups.amt.replace(',', '.'));
    let currency = match.groups.cur.toUpperCase();
    currency = CURRENCY_MAP[currency.toLowerCase()] || currency;
    if (amount > 0) return { amount, currency };
  }

  return null;
}

// ============ BRAND DETECTION ============

const normalize = (s: string) =>
  s.normalize('NFKD')
    .toLowerCase()
    .replace(/[^\p{Letter}\p{Number}]+/gu, '');

export const BRAND_MAP: Record<string, BrandInfo> = {
  // USA (30 brands)
  'walmart': { merchant: 'Walmart', category: 'Food' },
  'target': { merchant: 'Target', category: 'Shopping' },
  'cvs': { merchant: 'CVS', category: 'Health' },
  'walgreens': { merchant: 'Walgreens', category: 'Health' },
  'wholefoods': { merchant: 'Whole Foods', category: 'Food' },
  'traderjoes': { merchant: "Trader Joe's", category: 'Food' },
  'costco': { merchant: 'Costco', category: 'Food' },
  'kroger': { merchant: 'Kroger', category: 'Food' },
  'safeway': { merchant: 'Safeway', category: 'Food' },
  'publix': { merchant: 'Publix', category: 'Food' },
  'dunkin': { merchant: "Dunkin'", category: 'Food', aliases: ['dunkindonuts'] },
  'wendys': { merchant: "Wendy's", category: 'Food' },
  'tacobell': { merchant: 'Taco Bell', category: 'Food' },
  'chipotle': { merchant: 'Chipotle', category: 'Food' },
  'gap': { merchant: 'Gap', category: 'Shopping' },
  'oldnavy': { merchant: 'Old Navy', category: 'Shopping' },
  'macys': { merchant: "Macy's", category: 'Shopping' },
  'bestbuy': { merchant: 'Best Buy', category: 'Shopping' },
  'chevron': { merchant: 'Chevron', category: 'Transport' },
  'exxon': { merchant: 'Exxon', category: 'Transport' },
  'lyft': { merchant: 'Lyft', category: 'Transport' },

  // Europe (25 brands)
  'lidl': { merchant: 'LIDL', category: 'Food' },
  'aldi': { merchant: 'Aldi', category: 'Food' },
  'carrefour': { merchant: 'Carrefour', category: 'Food' },
  'tesco': { merchant: 'Tesco', category: 'Food' },
  'mercadona': { merchant: 'Mercadona', category: 'Food' },
  'dia': { merchant: 'Dia', category: 'Food' },
  'albertheijn': { merchant: 'Albert Heijn', category: 'Food' },
  'jumbo': { merchant: 'Jumbo', category: 'Food' },
  'edeka': { merchant: 'Edeka', category: 'Food' },
  'rewe': { merchant: 'Rewe', category: 'Food' },
  'zara': { merchant: 'Zara', category: 'Shopping' },
  'hm': { merchant: 'H&M', category: 'Shopping', aliases: ['handm'] },
  'pullbear': { merchant: 'Pull&Bear', category: 'Shopping' },
  'mango': { merchant: 'Mango', category: 'Shopping' },
  'bershka': { merchant: 'Bershka', category: 'Shopping' },
  'primark': { merchant: 'Primark', category: 'Shopping' },
  'mediamarkt': { merchant: 'Media Markt', category: 'Shopping' },
  'saturn': { merchant: 'Saturn', category: 'Shopping' },

  // UK (10 brands)
  'sainsburys': { merchant: "Sainsbury's", category: 'Food' },
  'asda': { merchant: 'Asda', category: 'Food' },
  'morrisons': { merchant: 'Morrisons', category: 'Food' },
  'waitrose': { merchant: 'Waitrose', category: 'Food' },
  'coop': { merchant: 'Co-op', category: 'Food' },
  'boots': { merchant: 'Boots', category: 'Health' },
  'superdrug': { merchant: 'Superdrug', category: 'Health' },

  // Ukraine (10 brands)
  'атб': { merchant: 'АТБ', category: 'Food' },
  'сільпо': { merchant: 'Сільпо', category: 'Food' },
  'silpo': { merchant: 'Сільпо', category: 'Food' },
  'novus': { merchant: 'Novus', category: 'Food' },
  'fozzy': { merchant: 'Fozzy', category: 'Food' },
  'ашан': { merchant: 'Ашан', category: 'Food' },
  'auchan': { merchant: 'Ашан', category: 'Food' },
  'вопак': { merchant: 'Вопак', category: 'Food' },
  'таврія': { merchant: 'Таврія В', category: 'Food' },

  // Global chains
  'starbucks': { merchant: 'Starbucks', category: 'Food' },
  'mcdonalds': { merchant: "McDonald's", category: 'Food' },
  'burgerking': { merchant: 'Burger King', category: 'Food' },
  'subway': { merchant: 'Subway', category: 'Food' },
  'kfc': { merchant: 'KFC', category: 'Food' },
  'pizzahut': { merchant: 'Pizza Hut', category: 'Food' },
  'dominos': { merchant: "Domino's", category: 'Food' },
  '7eleven': { merchant: '7-Eleven', category: 'Food', aliases: ['711', 'seveneleven'] },
  'familymart': { merchant: 'FamilyMart', category: 'Food' },
  'shell': { merchant: 'Shell', category: 'Transport' },
  'bp': { merchant: 'BP', category: 'Transport' },
  'uber': { merchant: 'Uber', category: 'Transport' },
};

export function detectBrand(text: string): BrandInfo | null {
  const normalized = normalize(text);

  for (const [key, info] of Object.entries(BRAND_MAP)) {
    if (normalized.includes(key)) return info;

    if (info.aliases?.some(alias => normalized.includes(alias))) {
      return info;
    }
  }

  return null;
}

// ============ DATE PARSING ============

const MONTH_NAMES: Record<string, number> = {
  // English
  'january': 0, 'jan': 0, 'february': 1, 'feb': 1, 'march': 2, 'mar': 2,
  'april': 3, 'apr': 3, 'may': 4, 'june': 5, 'jun': 5,
  'july': 6, 'jul': 6, 'august': 7, 'aug': 7, 'september': 8, 'sep': 8, 'sept': 8,
  'october': 9, 'oct': 9, 'november': 10, 'nov': 10, 'december': 11, 'dec': 11,
  // Spanish
  'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3, 'mayo': 4, 'junio': 5,
  'julio': 6, 'agosto': 7, 'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11,
  // Ukrainian
  'січень': 0, 'лютий': 1, 'березень': 2, 'квітень': 3, 'травень': 4, 'червень': 5,
  'липень': 6, 'серпень': 7, 'вересень': 8, 'жовтень': 9, 'листопад': 10, 'грудень': 11,
};

export function parseDate(text: string, today = new Date()): string {
  const lower = text.toLowerCase();

  // Relative: yesterday
  if (/yesterday|ayer|вчора/i.test(lower)) {
    return format(subDays(today, 1), 'yyyy-MM-dd');
  }

  // Relative: last [weekday]
  if (/last\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i.test(lower)) {
    const dayMap: Record<string, number> = {
      sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6
    };
    const targetDay = dayMap[RegExp.$1.toLowerCase()];
    const currentDay = today.getDay();
    const daysAgo = currentDay >= targetDay ? currentDay - targetDay : 7 - (targetDay - currentDay);
    return format(subDays(today, daysAgo), 'yyyy-MM-dd');
  }

  // Relative: N days/weeks/months ago
  const agoMatch = lower.match(/(\d+)\s+(day|week|month)s?\s+ago/);
  if (agoMatch) {
    const num = parseInt(agoMatch[1]);
    const unit = agoMatch[2];
    const days = unit === 'day' ? num : unit === 'week' ? num * 7 : num * 30;
    return format(subDays(today, days), 'yyyy-MM-dd');
  }

  // Absolute: "15 march 2025", "march 15", "15 march"
  const monthMatch = lower.match(/(\d{1,2})\s+([a-zа-яії]+)\s*(\d{4})?/);
  if (monthMatch) {
    const day = parseInt(monthMatch[1]);
    const monthStr = monthMatch[2];
    const year = monthMatch[3] ? parseInt(monthMatch[3]) : today.getFullYear();
    const month = MONTH_NAMES[monthStr];

    if (month !== undefined && day >= 1 && day <= 31) {
      return format(new Date(year, month, day), 'yyyy-MM-dd');
    }
  }

  // Absolute: DD/MM/YYYY or DD.MM.YYYY (European)
  const euroMatch = text.match(/(\d{1,2})[./](\d{1,2})[./](\d{4})/);
  if (euroMatch) {
    const day = parseInt(euroMatch[1]);
    const month = parseInt(euroMatch[2]);
    const year = parseInt(euroMatch[3]);
    return format(new Date(year, month - 1, day), 'yyyy-MM-dd');
  }

  // Default: today
  return format(today, 'yyyy-MM-dd');
}

// ============ KEYWORD DETECTION ============

const KEYWORDS: Record<string, { merchant: string; category: Category }> = {
  'coffee': { merchant: 'Coffee Shop', category: 'Food' },
  'кава': { merchant: 'Coffee Shop', category: 'Food' },
  'café': { merchant: 'Coffee Shop', category: 'Food' },
  'lunch': { merchant: 'Restaurant', category: 'Food' },
  'обід': { merchant: 'Restaurant', category: 'Food' },
  'dinner': { merchant: 'Restaurant', category: 'Food' },
  'вечеря': { merchant: 'Restaurant', category: 'Food' },
  'groceries': { merchant: 'Grocery Store', category: 'Food' },
  'продукти': { merchant: 'Grocery Store', category: 'Food' },
  'taxi': { merchant: 'Taxi', category: 'Transport' },
  'таксі': { merchant: 'Taxi', category: 'Transport' },
  'gas': { merchant: 'Gas Station', category: 'Transport' },
  'fuel': { merchant: 'Gas Station', category: 'Transport' },
  'parking': { merchant: 'Parking', category: 'Transport' },
  'hotel': { merchant: 'Hotel', category: 'Housing' },
  'готель': { merchant: 'Hotel', category: 'Housing' },
  'rent': { merchant: 'Rent', category: 'Housing' },
  'оренда': { merchant: 'Rent', category: 'Housing' },
};

export function detectKeyword(text: string): { merchant: string; category: Category } | null {
  const lower = text.toLowerCase();
  for (const [keyword, info] of Object.entries(KEYWORDS)) {
    if (lower.includes(keyword)) return info;
  }
  return null;
}

// ============ NOTE EXTRACTION ============

export function extractNote(text: string): string {
  let note = text
    // Remove amounts and currencies
    .replace(/\d+([.,]\d{1,2})?\s*(USD|EUR|GBP|UAH|[$€£₴грн])?/gi, '')
    // Remove dates
    .replace(/yesterday|ayer|вчора|last\s+\w+|ago|\d+[./]\d+[./]\d+/gi, '')
    .trim();

  // Remove matched brands
  for (const brand of Object.keys(BRAND_MAP)) {
    note = note.replace(new RegExp(brand, 'gi'), '');
  }

  // Remove keywords
  for (const keyword of Object.keys(KEYWORDS)) {
    note = note.replace(new RegExp(keyword, 'gi'), '');
  }

  return note.trim();
}

// ============ HELPERS FOR EDGE FUNCTION ============

export function getRelevantBrands(locale?: string, currency?: string): string[] {
  const regionBrands: Record<string, string[]> = {
    'en-US': ['Walmart', 'Target', 'CVS', 'Starbucks', 'Uber', "McDonald's", 'Whole Foods', 'Kroger'],
    'en-GB': ['Tesco', "Sainsbury's", 'Asda', 'Boots', 'Primark', 'Waitrose'],
    'uk': ['АТБ', 'Сільпо', 'Novus', 'Fozzy', 'Ашан'],
    'es': ['Mercadona', 'Carrefour', 'Dia', 'Lidl', 'Zara'],
    'de': ['Lidl', 'Aldi', 'Edeka', 'Rewe', 'Media Markt'],
    'nl': ['Albert Heijn', 'Jumbo', 'Lidl', 'Aldi'],
  };

  const global = ['Starbucks', "McDonald's", 'KFC', 'Uber', 'Shell', 'BP'];
  
  let regional: string[] = [];
  if (locale && regionBrands[locale]) {
    regional = regionBrands[locale];
  } else if (currency === 'UAH') {
    regional = regionBrands['uk'];
  } else if (currency === 'USD') {
    regional = regionBrands['en-US'];
  } else if (currency === 'GBP') {
    regional = regionBrands['en-GB'];
  } else {
    regional = regionBrands['de']; // Default to Europe
  }

  return [...regional, ...global].slice(0, 30);
}
